import { DateTime } from "luxon";

import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { Patient } from "@/Common/Models/Patient/Patient";
import { RelationshipLinks } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { QuebecObstetricalFileFormPageFour } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/QuebecObstetricalFileFormPageFour";
import { FamilyAntecedents } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/FamilyAntecedents";
import { PreviousPregnancies } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/PreviousPregnancies";
import { QuebecObstetricalFileFormPageOne } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/QuebecObstetricalFileFormPageOne";
import { SpousalInfo } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/SpousalInfo";
import { ComplementaryExaminations } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/ComplementaryExaminations";
import { ImmunitySerology } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/ImmunitySerology";
import { QuebecObstetricalFileFormPageThree } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/QuebecObstetricalFileFormPageThree";
import { QuebecObstetricalFileFormPageTwo } from "@/Patient/Models/ObstetricalFile/Quebec/PageTwo/QuebecObstetricalFileFormPageTwo";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";
import { BaseObstetricalFormCreator, ObstetricalFileParams } from "../BaseObstetricalFormCreator";

class QuebecObstetricalFileFormCreator extends BaseObstetricalFormCreator {
    public createObstetricalFileForm(params: ObstetricalFileParams): QuebecObstetricalFileForm {
        const previousQuebecObstetricalFileForm = params.previousObstetricalFile?.Form && params.previousObstetricalFile.ObstetricalFileType === ObstetricalFileType.QuebecForm
            ? params.previousObstetricalFile.getFormObject<QuebecObstetricalFileForm>()
            : null;

        const form = new QuebecObstetricalFileForm({
            Context: params.obstetricFormContext,
            PageOne: this.createPageOne(params.patient, params.consultationDate, previousQuebecObstetricalFileForm),
            PageTwo: new QuebecObstetricalFileFormPageTwo(),
            PageThree: this.createPageThree(previousQuebecObstetricalFileForm),
            PageFour: new QuebecObstetricalFileFormPageFour(),
            ObstetricalCounters: this.createObstetricalCounters(params.obstetricalCounters)
        });

        form.setDefaultWeightUnit(sessionStore.useMetricSystemAsDefault ? WeightUnit.Kilogram : WeightUnit.Pound);
        form.setDefaultHeightUnit(sessionStore.useMetricSystemAsDefault ? HeightUnit.Meter : HeightUnit.Inch);
        return form;
    }

    private createPageOne(
        patient: Patient,
        consultationDate: Date,
        previousQuebecObstetricalFileForm: QuebecObstetricalFileForm
    ): QuebecObstetricalFileFormPageOne {
        return new QuebecObstetricalFileFormPageOne({
            SpousalInfo: this.createSpousalInformation(patient, consultationDate),
            FamilyAntecedents: this.createFamilyAntecedents(previousQuebecObstetricalFileForm),
            PreviousPregnancies: this.createPreviousPregnancy(previousQuebecObstetricalFileForm)
        });
    }

    private createPageThree(previousQuebecObstetricalFileForm: QuebecObstetricalFileForm): QuebecObstetricalFileFormPageThree {
        return new QuebecObstetricalFileFormPageThree({
            ComplementaryExaminations: this.createComplementaryExaminations(previousQuebecObstetricalFileForm)
        });
    }

    private createComplementaryExaminations(previousQuebecObstetricalFileForm: QuebecObstetricalFileForm): ComplementaryExaminations {
        return new ComplementaryExaminations({
            ImmunitySerology: this.createImmunitySerology(previousQuebecObstetricalFileForm)
        });
    }

    private createImmunitySerology(previousQuebecObstetricalFileForm: QuebecObstetricalFileForm): ImmunitySerology {
        return new ImmunitySerology({
            HasRubella: previousQuebecObstetricalFileForm?.PageThree?.ComplementaryExaminations?.ImmunitySerology?.HasRubella ? true : null
        });
    }

    private createSpousalInformation(patient: Patient, consultationDate: Date): SpousalInfo {
        const spouseLookUp = cacheStore.GetRelationshipTypeLookups()?.firstOrDefault(p => p.LookupCode === RelationshipLinks.Spouse);
        const spouse = patient.Relationships?.firstOrDefault(p => p.RelationLookupId === spouseLookUp.LookupID);
        if (!spouse)
            return new SpousalInfo();

        const spouseDateOfBirth = LinkedContact.GetDateOfBirth(spouse);

        const personalType = cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE");
        const phoneType = cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE");
        const spousePersonalPhones = spouse.PhoneNumbers?.filter(p => p.ContactPointTypeLookupId === personalType.LookupID && p.PhoneTypeLookupId === phoneType.LookupID) ?? [];

        return new SpousalInfo({
            Name: LinkedContact.GetFullName(spouse),
            Age: spouseDateOfBirth ? Math.floor(DateTime.fromISO(consultationDate.toString()).diff(DateTime.fromISO(spouseDateOfBirth?.toString()), "years").years) : null,
            Description: spousePersonalPhones.any()
                ? `${cacheStore.GetString("PersonalDashPhoneLabel")} ${PhoneNumber.GetFormattedPhoneNumber(0, spousePersonalPhones, "{1} {2}-{3}")}`
                : null
        });
    }

    private createFamilyAntecedents(previousObstetricalFileForm: QuebecObstetricalFileForm): FamilyAntecedents {
        return new FamilyAntecedents(previousObstetricalFileForm?.PageOne?.FamilyAntecedents);
    }

    private createPreviousPregnancy(previousObstetricalFileForm: QuebecObstetricalFileForm): PreviousPregnancies {
        return new PreviousPregnancies(previousObstetricalFileForm?.PageOne?.PreviousPregnancies);
    }
}

export { QuebecObstetricalFileFormCreator };
